import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Check, Lock, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { STEPS, BLOCK_NAMES, isVisible, stepValid } from "../lib/onboardingSchema";
import { Field } from "../components/onboarding/Field";
import "../components/empieza/empieza.css";

const QV_LOGO = "https://customer-assets.emergentagent.com/job_athletic-edge-19/artifacts/7jyn9i1w_IMG_2453.PNG";
const PREFILL = { full_name: "player_name", age: "age", club: "team", positions: "positions" };
const fmtKnown = (v) => (Array.isArray(v) ? v.join(", ") : String(v));

const Shell = ({ children, onLogout }) => (
  <div className="emp-root" data-testid="onboarding-page">
    <div className="emp-bg" aria-hidden="true"><img src={QV_LOGO} alt="" className="emp-logo-big" /></div>
    <div className="emp-wrap">
      <div className="emp-head">
        <div className="emp-brand"><img src={QV_LOGO} alt="QV" /><div className="qv-display" style={{ fontSize: 18 }}>QV PERFORMANCE</div></div>
        {onLogout && <button className="emp-linkbtn" style={{ marginTop: 0 }} onClick={onLogout} data-testid="onboarding-logout">Salir</button>}
      </div>
      {children}
    </div>
  </div>
);

const KnownRow = ({ f, value, onEdit }) => (
  <div className="emp-field" style={{ marginBottom: 22 }}>
    <label style={{ fontSize: 13, color: "#e5e5e5", fontWeight: 600, textTransform: "none", letterSpacing: "0.04em" }}>{f.label}</label>
    <div className="emp-opt on" style={{ cursor: "default" }} data-testid={`onb-known-${f.k}`}>
      <Check size={16} /> <span style={{ flex: 1 }}>{fmtKnown(value)}</span>
      <button type="button" className="emp-linkbtn" style={{ marginTop: 0 }} onClick={onEdit} data-testid={`onb-known-edit-${f.k}`}><Pencil size={13} /> Cambiar</button>
    </div>
  </div>
);

const LeadSummary = ({ known }) => (
  <div className="emp-note" data-testid="onb-lead-summary">
    <div style={{ fontWeight: 700, marginBottom: 6 }}>Esto ya me lo contaste:</div>
    {known.goals && <div><b>Objetivo:</b> {fmtKnown(known.goals)}{known.goals_detail ? ` — ${known.goals_detail}` : ""}</div>}
    {known.obstacle && <div><b>Obstáculo:</b> {known.obstacle}</div>}
    {known.current_situation && <div><b>Situación:</b> {known.current_situation}</div>}
  </div>
);

const Review = ({ answers, known, onGo }) => (
  <div data-testid="onb-review">
    <div className="qv-overline">Último paso</div>
    <h1 className="emp-q">Revisa y envía</h1>
    <p className="emp-help">Puedes volver a cualquier bloque antes de enviar. Después de enviarlo, revisaré tus respuestas.</p>
    <div className="emp-opts">
      {Object.entries(BLOCK_NAMES).map(([b, name]) => {
        const idx = STEPS.findIndex((s) => s.b === b);
        const ok = STEPS.filter((s) => s.b === b).every((s) => stepValid(s, answers[b] || {}, known));
        return (
          <button key={b} type="button" className={`emp-opt${ok ? " on" : ""}`} onClick={() => onGo(idx)} data-testid={`onb-review-${b}`}>
            <span className="emp-tick">{ok && <Check size={14} strokeWidth={3} />}</span>
            <span style={{ flex: 1 }}>{b}. {name}</span>
            {!ok && <span style={{ fontSize: 12, color: "#f87171" }}>Falta algo</span>}
          </button>
        );
      })}
    </div>
  </div>
);

export default function OnboardingForm() {
  const { logout } = useAuth();
  const [st, setSt] = useState({ loading: true });
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState([]);
  const [step, setStep] = useState(-1);
  const [editing, setEditing] = useState({});
  const [saved, setSaved] = useState("");
  const [sending, setSending] = useState(false);
  const timers = useRef({});
  const latest = useRef({});

  const save = useCallback(async (block, data) => {
    try { await api.put("/me/onboarding/answers", { block, data }); setSaved("Guardado"); }
    catch (e) { setSaved(""); toast.error(formatErr(e)); }
  }, []);

  const queueSave = (block, data) => {
    latest.current[block] = data;
    setSaved("Guardando…");
    clearTimeout(timers.current[block]);
    timers.current[block] = setTimeout(() => save(block, latest.current[block]), 700);
  };

  useEffect(() => {
    let ignore = false;
    api.get("/me/onboarding").then(({ data }) => {
      if (ignore) return;
      const ans = data.answers || {};
      const A = { ...(ans.A || {}) };
      let changed = false;
      Object.entries(PREFILL).forEach(([k, src]) => {
        if (A[k] === undefined && data.known?.[src] != null) { A[k] = data.known[src]; changed = true; }
      });
      ans.A = A;
      if (changed && ["pending", "in_progress"].includes(data.status)) save("A", A);
      setAnswers(ans);
      setFiles(data.files || []);
      setStep(ans.meta?.step ?? -1);
      setSt({ loading: false, ...data });
    }).catch((e) => { if (!ignore) setSt({ loading: false, error: formatErr(e) }); });
    return () => { ignore = true; };
  }, [save]);

  const go = (n) => {
    Object.keys(timers.current).forEach((b) => { clearTimeout(timers.current[b]); if (latest.current[b]) save(b, latest.current[b]); latest.current[b] = null; });
    setStep(n);
    save("meta", { step: n });
    window.scrollTo({ top: 0 });
  };

  const setField = (b, k, v) => {
    const data = { ...(answers[b] || {}), [k]: v };
    setAnswers({ ...answers, [b]: data });
    queueSave(b, data);
  };

  const submit = async () => {
    setSending(true);
    try { await api.post("/me/onboarding/submit"); setSt({ ...st, status: "completed" }); window.scrollTo({ top: 0 }); }
    catch (e) { toast.error(formatErr(e)); } finally { setSending(false); }
  };

  if (st.loading) return <Shell><div style={{ color: "#a3a3a3", paddingTop: 40 }}>Cargando…</div></Shell>;
  if (st.error) return <Shell onLogout={logout}><div style={{ paddingTop: 40 }} data-testid="onboarding-error"><h1 className="emp-q">Sin acceso</h1><p className="emp-help">{st.error}</p></div></Shell>;
  if (!["pending", "in_progress"].includes(st.status)) return (
    <Shell onLogout={logout}>
      <div className="emp-done" data-testid="onboarding-submitted">
        <div className="emp-done-icon"><Check size={38} strokeWidth={3} /></div>
        <h1 className="emp-q">¡Formulario enviado!</h1>
        <p className="emp-help" style={{ fontSize: 16, color: "#d4d4d4" }}>Gracias, {st.player_name?.split(" ")[0]}. He recibido toda tu información. La revisaré y te avisaré cuando tu planificación esté lista.</p>
        <div className="emp-note" data-testid="onboarding-plan-pending">Estado de tu planificación: <b>pendiente de revisión por tu entrenador</b>.</div>
      </div>
    </Shell>
  );

  if (step < 0) return (
    <Shell onLogout={logout}>
      <div className="emp-done" data-testid="onboarding-welcome">
        <div className="qv-overline">Incorporación{st.plan_name ? ` · ${st.plan_name}` : ""}</div>
        <h1 className="emp-q">Hola, {st.player_name?.split(" ")[0]}</h1>
        <p className="emp-help" style={{ whiteSpace: "pre-wrap", fontSize: 15, color: "#d4d4d4" }} data-testid="onboarding-welcome-message">{st.welcome_message}</p>
        <button className="emp-btn" style={{ flex: "none" }} onClick={() => go(0)} data-testid="onboarding-start">Empezar <ArrowRight size={18} /></button>
      </div>
    </Shell>
  );

  const isReview = step >= STEPS.length;
  const cur = STEPS[step];
  const data = cur ? answers[cur.b] || {} : {};
  const known = st.known || {};

  return (
    <Shell onLogout={logout}>
      <div className="emp-progress" data-testid="onboarding-progress"><div style={{ width: `${(Math.min(step + 1, STEPS.length + 1) / (STEPS.length + 1)) * 100}%` }} /></div>
      <div className="emp-step-count" style={{ display: "flex", justifyContent: "space-between" }}>
        <span data-testid="onboarding-step-count">Paso {step + 1} de {STEPS.length + 1}</span>
        <span data-testid="onboarding-saved" style={{ color: saved === "Guardado" ? "#4ade80" : "#a3a3a3" }}>{saved}</span>
      </div>
      <div key={step} className="emp-body">
        {isReview ? <Review answers={answers} known={known} onGo={go} /> : (
          <>
            <div className="qv-overline">Bloque {cur.b} · {BLOCK_NAMES[cur.b]}</div>
            <h1 className="emp-q" data-testid="onboarding-step-title">{cur.title}</h1>
            {cur.health && (
              <div className="emp-note" data-testid="onb-health-note">
                <Lock size={13} style={{ verticalAlign: -2 }} /> Información confidencial: solo la ve tu entrenador. Esto no es una valoración médica; ante cualquier síntoma, consulta con un profesional sanitario.
              </div>
            )}
            {cur.summary && (known.goals || known.obstacle || known.current_situation) && <LeadSummary known={known} />}
            {cur.fields.filter((f) => isVisible(f, data, known)).map((f) =>
              f.known && known[f.known] != null && JSON.stringify(data[f.k]) === JSON.stringify(known[f.known]) && !editing[f.k]
                ? <KnownRow key={f.k} f={f} value={data[f.k]} onEdit={() => setEditing({ ...editing, [f.k]: true })} />
                : <Field key={f.k} f={f} value={data[f.k]} data={data} onChange={(v) => setField(cur.b, f.k, v)} files={files} onFiles={setFiles} />
            )}
          </>
        )}
      </div>
      <div className="emp-nav">
        <button type="button" className="emp-btn ghost" onClick={() => go(step - 1)} aria-label="Atrás" data-testid="onboarding-back"><ArrowLeft size={20} /></button>
        {isReview ? (
          <button type="button" className="emp-btn" disabled={sending} onClick={submit} data-testid="onboarding-submit">{sending ? "Enviando…" : "Enviar formulario"}</button>
        ) : (
          <button type="button" className="emp-btn" disabled={!stepValid(cur, data, known)} onClick={() => go(step + 1)} data-testid="onboarding-next">Siguiente <ArrowRight size={18} /></button>
        )}
      </div>
      <button type="button" className="emp-linkbtn" style={{ alignSelf: "center" }} data-testid="onboarding-later"
        onClick={() => { go(step); toast.success("Guardado. Vuelve cuando quieras entrando con tu email y contraseña."); }}>
        Continuar más tarde
      </button>
    </Shell>
  );
}
